import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FileUploadComponent } from '../upload/file-upload/file-upload.component';
import { PpwrDocumentDto, PpwrDocumentSource, PpwrDocumentType } from 'src/app/data-transfer/entities/doc-entities/ppwr-document-dto';
import { FileApiService } from 'src/app/data-transfer/services/file-api-service';
import { TranslocoService } from '@jsverse/transloco';
import { MatDialog } from '@angular/material/dialog';
import { DeleteDialogData, DeleteItemDialogComponent } from 'src/app/components/dialogs/delete-item-dialog/delete-item-dialog.component';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { DialogActions } from 'src/app/model/dictionary';
import { Subscription } from 'rxjs';
import { SimpleDialogData } from '../../dialogs/simple-alert-dialog/simple-alert-dialog.component';

@Component({
  selector: 'app-ppwr-documents',
  templateUrl: './ppwr-documents.component.html',
  styleUrls: ['./ppwr-documents.component.scss'],
})
export class PpwrDocumentsComponent extends FileUploadComponent implements OnInit, OnDestroy {
  @Input() documents: PpwrDocumentDto[] = [];
  @Output() documentsChange = new EventEmitter<PpwrDocumentDto[]>();

  PpwrDocumentType = PpwrDocumentType;
  PpwrDocumentSource = PpwrDocumentSource;

  readonly documentTypeOptions = [
    {
      value: PpwrDocumentType.DoC,
      labelKey: 'ppwrDocuments.typeDoc',
      cssClass: 'doc',
    },
    {
      value: PpwrDocumentType.TD,
      labelKey: 'ppwrDocuments.typeTd',
      cssClass: 'td',
    },
  ];

  newFiles: File[] = [];

  private idSubscription?: Subscription;

  constructor(
    protected override dialog: MatDialog,
    private readonly fileApiService: FileApiService,
    private readonly translateService: TranslocoService
  ) {
    super(dialog);
  }

  ngOnInit(): void {
    super.setInitialNamesLength();
    super.setExistingNamesLength(this.documents);
  }

  onFileSelected(event: Event, documentType: PpwrDocumentType): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    if (file.type !== 'application/pdf') {
      this.showDialog({
        title: file.name,
        messages: [this.translateService.translate('errors.formatNotSupported')],
        icon: 'error',
      });
      return;
    }

    let assignedName = file.name;
    const listOfNames = this.documents.map(d => d.name);
    if (listOfNames.includes(file.name)) {
      assignedName = this.appendTextToDuplicateNameRecursively(listOfNames, file.name, 0);
      this.newFiles.push(new File([file], assignedName, { type: file.type }));
    } else {
      this.newFiles.push(file);
    }

    const position = this.documents.length;
    this.resizeInput(position);
    this.documents.push({
      index: position,
      name: assignedName,
      documentType,
      source: PpwrDocumentSource.UserUploaded,
      documentId: 0,
      description: '',
    });

    this.documentsChange.emit(this.documents);
    input.value = '';
    this.filesEdited.emit(true);
  }

  onNameEditClicked(index: number): void {
    super.onNameEdited(this.documents, index, this.getDuplicateNamesDialogData());
  }

  onDocumentTypeChange(ppwrdoc: PpwrDocumentDto, newType: PpwrDocumentType): void {
    if (ppwrdoc.documentType === newType) return;

    ppwrdoc.documentType = newType;
    this.documentsChange.emit(this.documents);
    this.filesEdited.emit(true);
  }

  getDocumentTypeLabel(type: PpwrDocumentType): string {
    const option = this.documentTypeOptions.find(option => option.value === type)!;
    return this.translateService.translate(option.labelKey);
  }

  getDocumentTypeCssClass(type: PpwrDocumentType): string {
    return this.documentTypeOptions.find(option => option.value === type)!.cssClass;
  }

  private getDuplicateNamesDialogData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.warning'),
      messages: [this.translateService.translate('warnings.duplicatePdfNames')],
      icon: 'warning',
    };
  }

  onViewDocumentClick(ppwrdoc: PpwrDocumentDto): void {
    if (ppwrdoc.documentId === 0) {
      const file = this.newFiles.find(f => f.name === ppwrdoc.name);
      if (!file) return;
      const url = window.URL.createObjectURL(file);
      window.open(url, '_blank');
    } else {
      this.idSubscription = this.fileApiService.getPdfById(ppwrdoc.documentId).subscribe(blob => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      });
    }
  }

  onDeleteClick(ppwrdoc: PpwrDocumentDto): void {
    const dialogData: DeleteDialogData = {
      dialogHeader: this.translateService.translate('pdf.deleteHeader'),
      dialogText: this.translateService.translate('pdf.deleteText'),
    };

    this.dialogSubscription = this.dialog
      .open(DeleteItemDialogComponent, getDialogConfig(dialogData, '500px'))
      .afterClosed()
      .subscribe(result => {
        if (result.event === DialogActions.DELETE) {
          this.deleteDocument(ppwrdoc);
        }
      });
  }

  private deleteDocument(ppwrdoc: PpwrDocumentDto): void {
    if (ppwrdoc.documentId === 0) {
      this.newFiles = this.newFiles.filter(f => f.name !== ppwrdoc.name);
    }
    this.documents = this.documents.filter(d => d.name !== ppwrdoc.name);
    this.documentsChange.emit(this.documents);
    this.filesEdited.emit(true);
  }

  public getNewPpwrFilesFormData(): FormData {
    return super.getNewFilesFormData(this.newFiles, 'files');
  }

  public updateDocumentIdsAfterSaving(docIds: number[]): void {
    for (let i = 0; i < docIds.length; i++) {
      if (this.newFiles[i]) {
        const doc = this.documents.find(d => d.name === this.newFiles[i].name);
        if (doc) doc.documentId = docIds[i];
      }
    }
    this.newFiles = [];
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.idSubscription?.unsubscribe();
  }
}
